import { Box } from "@mui/material";
import type { CSSProperties, RefObject } from "react";
import { currencySymbol, money } from "../lib/format";
import type { Account, Category, Member, Transaction } from "../lib/types";
import { LinkedTransactionGroup, TransactionRow, transactionAmountText } from "./TransactionRow";

function visibleGroupedTransactions(transactions: Transaction[]) {
  const loadedIds = new Set(transactions.map((transaction) => transaction.id));
  const grouped = new Map<string, Map<string, Transaction[]>>();
  for (const transaction of transactions) {
    if (transaction.linked_transaction_id && transaction.type === "income" && loadedIds.has(transaction.linked_transaction_id)) continue;
    const date = new Date(transaction.occurred_at);
    const month = date.toLocaleString(undefined, { month: "long", year: "numeric" });
    const day = date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    if (!grouped.has(month)) grouped.set(month, new Map());
    const monthGroup = grouped.get(month)!;
    if (!monthGroup.has(day)) monthGroup.set(day, []);
    monthGroup.get(day)!.push(transaction);
  }
  return Array.from(grouped.entries());
}

function periodTotal(transactions: Transaction[]) {
  return transactions.reduce((total, transaction) => {
    if (transaction.linked_transaction_id) return total;
    const amount = Number(transaction.amount || 0);
    if (!Number.isFinite(amount)) return total;
    if (transaction.type === "expense") return total - amount;
    if (transaction.type === "income" || transaction.type === "correction") return total + amount;
    return total;
  }, 0);
}

function signedMoney(value: number) {
  return `${value < 0 ? "-" : "+"}${money(Math.abs(value))}`;
}

type TransactionListProps = {
  transactions: Transaction[];
  accounts: Account[];
  categories: Category[];
  members: Member[];
  baseCurrency: string;
  onOpen: (transaction: Transaction) => void;
  onOpenId: (id: string) => void;
  hasMore?: boolean;
  loadMoreRef?: RefObject<HTMLDivElement | null>;
};

export function TransactionList({ transactions, accounts, categories, members, baseCurrency, onOpen, onOpenId, hasMore = false, loadMoreRef }: TransactionListProps) {
  const groupedTransactions = visibleGroupedTransactions(transactions);
  const transactionsById = new Map(transactions.map((transaction) => [transaction.id, transaction]));
  const amountColumnWidth = Math.max(8, ...transactions.map((transaction) => transactionAmountText(transaction).length));

  return (
    <Box className="list" style={{ "--record-amount-ch": `${amountColumnWidth}ch` } as CSSProperties}>
      {groupedTransactions.map(([month, days]) => {
        const monthTransactions = Array.from(days.values()).flat();
        const total = periodTotal(monthTransactions);
        const currency = monthTransactions[0]?.currency || baseCurrency;
        return (
          <Box className="record-group" key={month}>
            <Box className="record-month-header">
              <h3>{month}</h3>
              <strong className={`record-period-total ${total < 0 ? "amount-expense" : "amount-income"}`}>
                <span aria-hidden="true">Σ</span>
                <b>{signedMoney(total).replace(/[+-]/, (sign) => `${sign}${currencySymbol(currency)}`)}</b>
              </strong>
            </Box>
            {Array.from(days.entries()).map(([day, dayTransactions]) => (
              <Box className="record-day" key={day}>
                <Box className="record-day-header"><h4>{day}</h4></Box>
                {dayTransactions.map((transaction) => transaction.linked_transaction_id ? (
                  <LinkedTransactionGroup
                    key={transaction.id}
                    primary={transaction}
                    partner={transactionsById.get(transaction.linked_transaction_id) ?? transaction.linked_transaction ?? null}
                    accounts={accounts}
                    categories={categories}
                    members={members}
                    onOpen={onOpen}
                    onOpenId={onOpenId}
                  />
                ) : (
                  <TransactionRow key={transaction.id} transaction={transaction} accounts={accounts} categories={categories} members={members} onClick={() => onOpen(transaction)} />
                ))}
              </Box>
            ))}
          </Box>
        );
      })}
      {hasMore && loadMoreRef ? <Box ref={loadMoreRef} className="records-load-sentinel" aria-hidden="true" /> : null}
    </Box>
  );
}
